import { useState, useEffect } from 'react';
import { InfoWindow, Marker, OverlayView } from '@react-google-maps/api';
import { Box, Tooltip } from '@mui/material';
import { LocationOff, NightsStay, PinDrop } from '@mui/icons-material';
import { MapMarkerProps } from './interface';

interface ScooterMarkerWithBadgeProps extends MapMarkerProps {
  hasInactivityBadge?: boolean;
  hasNoCoordinatesBadge?: boolean;
  hasForceLocationBadge?: boolean;
}

const ScooterMarkerWithBadge = ({
  position,
  children,
  icon,
  forceInfoWindowOpen,
  batteryLevel = '',
  zIndex,
  hasInactivityBadge = false,
  hasNoCoordinatesBadge = false,
  hasForceLocationBadge = false
}: ScooterMarkerWithBadgeProps) => {
  const [open, setOpen] = useState(false);
  
  useEffect(() => {
    setOpen(Boolean(forceInfoWindowOpen));
  }, [forceInfoWindowOpen]);
  
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  // Видаляємо "*" (інактивність) та "!" (немає координат) незалежно від порядку
  let cleanBattery = String(batteryLevel);
  while (cleanBattery.startsWith('*') || cleanBattery.startsWith('!')) {
    if (cleanBattery.startsWith('*')) {
      cleanBattery = cleanBattery.substring(1);
    } else if (cleanBattery.startsWith('!')) {
      cleanBattery = cleanBattery.substring(1);
    }
  }
  // Також видаляємо "24h " якщо воно є
  if (hasInactivityBadge) {
    cleanBattery = cleanBattery.replace('24h ', '');
  }

  return (
    <>
      <Marker
        icon={icon}
        position={position}
        onClick={onOpen}
        label={{
          text: String(cleanBattery ?? ''),
          fontSize: '11px',
          fontWeight: '600',
          color: '#FFFFFF'
        }}
        {...(zIndex ? { zIndex } : {})}
      >
        {open && (
          <InfoWindow position={position} onCloseClick={onClose}>
            {children}
          </InfoWindow>
        )}
      </Marker>

      {hasInactivityBadge && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <Tooltip 
            title="Самокат без оренди більше 24 годин"
            arrow
            placement="top"
          >
            <Box
              sx={{
                position: 'absolute',
                transform: 'translate(4px, -44px)',
                pointerEvents: 'auto',
                cursor: 'pointer',
                zIndex: zIndex ? zIndex + 1 : 1,
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: '#1E3A5F',
                border: '2px solid #FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}
            >
              <NightsStay sx={{ fontSize: 11, color: '#FFD700' }} />
            </Box>
          </Tooltip>
        </OverlayView>
      )}

      {hasForceLocationBadge && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <Tooltip
            title="Примусова локація"
            arrow
            placement="top"
          >
            <Box
              sx={{
                position: 'absolute',
                transform: 'translate(-22px, -28px)',
                pointerEvents: 'auto',
                cursor: 'pointer',
                zIndex: zIndex ? zIndex + 2 : 2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: '#D32F2F',
                border: '2px solid #FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}
            >
              <PinDrop sx={{ fontSize: 9, color: '#FFFFFF' }} />
            </Box>
          </Tooltip>
        </OverlayView>
      )}

      {hasNoCoordinatesBadge && (
        <OverlayView
          position={position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <Tooltip 
            title="Координати не оновлювались"
            arrow
            placement="top"
          >
            <Box
              sx={{
                position: 'absolute',
                transform: 'translate(-22px, -44px)',
                pointerEvents: 'auto',
                cursor: 'pointer',
                zIndex: zIndex ? zIndex + 1 : 1,
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: '#D32F2F',
                border: '2px solid #FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}
            >
              <LocationOff sx={{ fontSize: 11, color: '#FFFFFF' }} />
            </Box>
          </Tooltip>
        </OverlayView>
      )}
    </>
  );
};

export default ScooterMarkerWithBadge;
